import CasesApi from "@/api/cases";
import {
  Option,
  SelectBox,
  SelectBoxProps,
  styled,
  useTheme,
} from "@monolith-forensics/monolith-ui";
import { useInfiniteQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useDebouncedCallback } from "use-debounce";

type CaseRecord = {
  case_id: number;
  uuid?: string;
  case_number: string;
  case_ref?: string | null;
};

export type CaseSelectorProps = Omit<
  SelectBoxProps,
  | "data"
  | "renderOption"
  | "OptionTooltip"
  | "searchFn"
  | "onScroll"
  | "searchable"
  | "loading"
  | "defaultValue"
  | "value"
> & {
  defaultValue?: Option;
  value?: Option;
};

const TooltipContent = styled(
  ({ className, data }: { className?: string; data: CaseRecord }) => {
    return (
      <div className={className}>
        <div className="detail-title">Case Details</div>
        <div className="detail-item">
          <div className="detail-label">Case Number</div>
          <div className="detail-value">{data.case_number || "None"}</div>
        </div>
        <div className="detail-item">
          <div className="detail-label">Case Name</div>
          <div className="detail-value">{data.case_ref || "None"}</div>
        </div>
        <div className="detail-item">
          <div className="detail-label">Case UUID</div>
          <div className="detail-value">{data?.uuid || "None"}</div>
        </div>
      </div>
    );
  }
)`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: fit-content;
  max-width: 300px;

  .detail-title {
    font-weight: bold;
    font-size: 15px;
  }
  .detail-item {
    display: flex;
    flex-direction: column;
  }

  .detail-label {
    font-weight: bold;
    min-width: 150px;
    color: ${({ theme }) => theme.palette.text.secondary};
  }

  .detail-value {
    color: ${({ theme }) => theme.palette.text.primary};
  }
`;

// Render like RoleSelector via renderOption prop
const CaseOptionComponent = ({ option }: { option: Partial<Option> }) => {
  const theme = useTheme();
  if (typeof option === "string") return <div>{option}</div>;
  const record = (option.data || {}) as CaseRecord;
  return (
    <div style={{ padding: "5px 0" }}>
      <div style={{ fontWeight: 500 }}>
        {record.case_number || option.label}
      </div>
      <div style={{ fontSize: "12px", color: theme.palette.text.secondary }}>
        {record.case_ref || ""}
      </div>
    </div>
  );
};

export const CaseSelector: React.FC<CaseSelectorProps> = (props) => {
  const [search, setSearch] = useState("");

  const query = {
    search,
    data_only: false,
    pageSize: 100,
  };

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading } =
    useInfiniteQuery({
      queryKey: ["cases", "list", query],
      queryFn: ({ pageParam = 1 }) => {
        return CasesApi.getCases({
          ...query,
          page: pageParam,
        });
      },
      getNextPageParam: (lastPage) => lastPage.nextPage,
      getPreviousPageParam: (firstPage: any) => {
        if (firstPage.page - 1 === 0) return null;
        return firstPage.page - 1;
      },
      initialPageParam: 1,
    });

  const records: CaseRecord[] =
    data?.pages?.reduce((acc, page) => {
      return [...acc, ...page.data];
    }, []) || [];

  const debouncedFetchNextPage = useDebouncedCallback(() => {
    if (isFetchingNextPage) return;
    if (!hasNextPage) return;
    fetchNextPage();
  }, 50);

  const debouncedSearch = useDebouncedCallback(
    (val: string) => setSearch(val || ""),
    300
  );

  const handleScroll = (e: React.UIEvent<HTMLElement>) => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (
      scrollHeight - scrollTop <=
      clientHeight + 100 * ((data?.pages?.length as number) || 0)
    ) {
      debouncedFetchNextPage();
    }
  };

  // Combine number and name in label for client-side search by either
  const options: Option[] = records.map((item) => ({
    label: `${item.case_number}${item.case_ref ? ` ${item.case_ref}` : ""}`,
    value: item.case_id,
    data: item,
  }));

  return (
    <SelectBox
      placeholder="Select or search for a case..."
      {...props}
      data={options}
      onScroll={handleScroll}
      searchable={true}
      searchFn={debouncedSearch}
      loading={isLoading}
      renderOption={(item) => {
        if (typeof item === "string") {
          return <div>{item}</div>;
        }
        return <CaseOptionComponent option={item} />;
      }}
      OptionTooltip={TooltipContent}
    />
  );
};
